import { cardPaint } from "@/components/shapes/shared/card-paint";
import type { WidgetFaceProps } from "@/components/shapes/shared/widget-face";
import {
  WIDGET_DISPLAY,
  WIDGET_MUTED,
  WidgetEmpty,
  WidgetFaceRoot,
  WidgetRule,
} from "@/components/shapes/shared/widget-face-parts";
import { Icons } from "@/components/ui/icons";
import { useT } from "@/i18n";
import { framesFlightMessages } from "@/i18n/messages/frames-flight";
import { cn } from "@/lib/cn";
import { previewSource } from "@/lib/frame-preview-source";
import {
  flightFields,
  hasFlight,
  type FlightDetail,
  type FlightEndpoint,
} from "./flight-content";

/** The large face's facts: three across, two rows. */
const MAX_FACTS = 6;

/** A label over its value (mockup `.kv`, the medium foot inline). */
function Fact({
  label,
  value,
  inline = false,
}: {
  label: string;
  value: string;
  inline?: boolean;
}) {
  if (inline) {
    return (
      <p className="min-w-0 truncate">
        <span className={WIDGET_MUTED}>{label}</span>{" "}
        <b className="font-semibold">{value}</b>
      </p>
    );
  }
  return (
    <p className="min-w-0">
      <span className={cn("block truncate", WIDGET_MUTED)}>{label}</span>
      <b className="block truncate text-appkit-subhead font-semibold">
        {value}
      </b>
    </p>
  );
}

/** One end of the route: its code over its city (and time). */
function End({
  endpoint,
  large,
  end = false,
}: {
  endpoint: FlightEndpoint;
  large: boolean;
  end?: boolean;
}) {
  const place = large
    ? endpoint.city
    : [endpoint.city, endpoint.time].filter(Boolean).join(" ");
  return (
    <div className={cn("min-w-0", end && "text-right")}>
      <p className={large ? WIDGET_DISPLAY.xl : WIDGET_DISPLAY.l}>
        {endpoint.code || "—"}
      </p>
      {place && <p className={cn("truncate", WIDGET_MUTED)}>{place}</p>}
    </div>
  );
}

/** From and to with the dashed path between them. */
function Route({
  from,
  to,
  large,
}: {
  from: FlightEndpoint;
  to: FlightEndpoint;
  large: boolean;
}) {
  return (
    <div
      className={cn(
        "flex items-end justify-between",
        large ? "mt-3.5" : "mt-1.5",
      )}
      data-testid="flight-widget-route"
    >
      <End endpoint={from} large={large} />
      <div
        aria-hidden="true"
        className="mx-2.5 mb-[9px] min-w-4 flex-1 border-t-[1.5px] border-dashed border-[color:var(--widget-muted,var(--card-muted))]"
      />
      <End endpoint={to} large={large} end />
    </div>
  );
}

/**
 * A flight on the desktop, in the flight paint. Small: the departure, where
 * it goes, its time and gate. Medium: the route with cities and times,
 * gate and seat. Large: the route and the boarding pass facts.
 */
export function FlightWidgetFace({ frame, size }: WidgetFaceProps) {
  const t = useT(framesFlightMessages);
  const fields = flightFields(previewSource(frame));
  if (!hasFlight(fields)) {
    return <WidgetEmpty frame={frame} size={size} message={t("noFlight")} />;
  }
  const { from, to, details } = fields;
  const paint = { name: "flight", background: cardPaint(frame).background };
  const detail = (key: FlightDetail["key"]) =>
    details.find((entry) => entry.key === key)?.value ?? "";
  const heading = [fields.heading, fields.statusTag]
    .filter(Boolean)
    .join(" · ");

  if (size === "small") {
    const gate = detail("gate");
    return (
      <WidgetFaceRoot frame={frame} size={size} paint={paint}>
        <p className={cn("flex min-w-0 items-center gap-1.5", WIDGET_MUTED)}>
          <Icons.Plane
            className="h-[13px] w-[13px] shrink-0"
            aria-hidden="true"
          />
          <span className="truncate">{fields.heading || fields.statusTag}</span>
        </p>
        <p className={cn("mt-1", WIDGET_DISPLAY.l)}>{from.code || "—"}</p>
        {to.code && (
          <p className={WIDGET_MUTED}>
            →{" "}
            <b className="font-semibold text-[color:var(--widget-ink,var(--card-ink))]">
              {to.code}
            </b>
          </p>
        )}
        <div className="mt-auto flex min-w-0 justify-between gap-2">
          <span className={cn("truncate", WIDGET_MUTED)}>{from.time}</span>
          {gate && <Fact label={t("gate")} value={gate} inline />}
        </div>
      </WidgetFaceRoot>
    );
  }

  if (size === "medium") {
    const foot = (["gate", "seat"] as const)
      .map((key) => ({ key, value: detail(key) }))
      .filter((entry) => entry.value);
    return (
      <WidgetFaceRoot frame={frame} size={size} paint={paint}>
        <div className={cn("flex min-w-0 justify-between gap-3", WIDGET_MUTED)}>
          <span className="truncate">{heading}</span>
          <span className="shrink-0">{detail("duration")}</span>
        </div>
        <Route from={from} to={to} large={false} />
        {foot.length > 0 && (
          <div className="mt-auto flex gap-[22px]">
            {foot.map((entry) => (
              <Fact
                key={entry.key}
                label={t(entry.key)}
                value={entry.value}
                inline
              />
            ))}
          </div>
        )}
      </WidgetFaceRoot>
    );
  }

  const facts = [
    { key: "departs", label: t("departs"), value: from.time },
    { key: "arrives", label: t("arrives"), value: to.time },
    ...details.map((entry) => ({
      key: entry.key,
      label: t(entry.key),
      value: entry.value,
    })),
  ]
    .filter((fact) => fact.value)
    .slice(0, MAX_FACTS);
  return (
    <WidgetFaceRoot frame={frame} size={size} paint={paint}>
      <div className={cn("flex min-w-0 justify-between gap-3", WIDGET_MUTED)}>
        <span className="truncate">{heading}</span>
        <Icons.Plane
          className="h-[15px] w-[15px] shrink-0"
          aria-hidden="true"
        />
      </div>
      <Route from={from} to={to} large />
      {facts.length > 0 && (
        <>
          <WidgetRule className="mb-3 mt-[18px]" />
          <div
            className="grid grid-cols-3 gap-2.5"
            data-testid="flight-widget-facts"
          >
            {facts.map((fact) => (
              <Fact key={fact.key} label={fact.label} value={fact.value} />
            ))}
          </div>
        </>
      )}
    </WidgetFaceRoot>
  );
}
